import { useState, useRef, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../utils/authConfig";
import { useUseCase } from "../../context/UseCaseContext/UseCaseContext";
import { IndexedDB } from "../indexedDB/IndexedDB";
import {
  uploadFileApi,
  deleteUploadedFileApi,
  listUploadedFilesApi,
  FileProgressUpdate,
} from "../../api";

const LOCAL_DEV = import.meta.env.VITE_LOCAL_DEV;
const MAX_IMAGES_PER_SESSION = 10;

// --- Extension Constants ---
export const imageExtensions = [".png", ".jpg", ".jpeg"];
const pdfExtensions = [".pdf"];
const wordExtensions = [".docx"];
const pptExtensions = [".pptx"];
const msgExtensions = [".msg"];

const allUploadExtensions = [
  ...imageExtensions,
  ...pdfExtensions,
  ...wordExtensions,
  ...pptExtensions,
  ...msgExtensions,
];

const mimeTypes = [
  "image/*",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

export const allAcceptedUploadTypes = [...mimeTypes, ...allUploadExtensions];

const defaultPhases: string[] = [
  "Initializing...",
  "Uploading...",
  "Processing...",
  "Done.",
];

export const useFileUpload = (
  sessionState: string,
  onUploadComplete: (
    paths: string[],
    names: string[],
    images: string[],
  ) => void,
  onDocumentDelete: (path: string, name: string) => void,
) => {
  const { selectedUseCase } = useUseCase();
  const client = useMsal().instance;
  const [indexedDBInstance, setIndexedDBInstance] = useState(
    new IndexedDB("chat-database", "chat-history", selectedUseCase),
  );

  // State
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [isHandling, setIsHandling] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [phases, setPhases] = useState<string[]>(defaultPhases);
  const [currentPhase, setCurrentPhase] = useState(0);

  // Warning system
  const uploadWarningRef = useRef<string | null>(null);
  const [warningMsg, setWarningMsg] = useState<string | null>(null);

  // Initial Load
  useEffect(() => {
    setIndexedDBInstance(
      new IndexedDB("chat-database", "chat-history", selectedUseCase),
    );
  }, [selectedUseCase]);

  // Files of this chat in the index. Only the image limit of an upload needs them, so they are
  // listed when an upload contains images, not whenever the chat changes.
  const refreshFiles = useCallback(async (): Promise<string[]> => {
    if (selectedUseCase !== "GENERIC" && selectedUseCase !== "REGIS") return [];
    try {
      const token = await getToken(client);
      if (!token && !LOCAL_DEV) return [];
      const files = await listUploadedFilesApi(
        selectedUseCase,
        token,
        sessionState,
      );
      setUploadedFiles(files);
      return files;
    } catch (e) {
      console.error("Failed to list files", e);
      return [];
    }
  }, [client, selectedUseCase, sessionState]);

  // Handlers
  const handleUploadFiles = async (
    filesInput: FileList | File[] | null | undefined,
  ) => {
    const fileArray = filesInput
      ? Array.from(filesInput as FileList | File[])
      : [];
    if (!fileArray.length) return;

    uploadWarningRef.current = null;
    setWarningMsg(null);
    setIsHandling(true);
    setError(undefined);

    setPhases([...defaultPhases]);
    setCurrentPhase(0);

    const uploadedPaths: string[] = [];
    const uploadedFilenames: string[] = [];
    const uploadedImages: string[] = [];
    const totalFiles = fileArray.length;

    const isFileAnImage = (filename: string) =>
      imageExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
    const existingImageCount = fileArray.some((f) => isFileAnImage(f.name))
      ? (await refreshFiles()).filter(isFileAnImage).length
      : 0;

    try {
      const idToken = await getToken(client);
      if (!LOCAL_DEV && !idToken)
        throw new Error("No authentication token available");

      for (let i = 0; i < totalFiles; i++) {
        const file = fileArray[i];
        const prefix = totalFiles > 1 ? `(${i + 1}/${totalFiles}) ` : "";
        let filepath = "";
        let filename = "";
        let image_filename = "";

        if (isFileAnImage(file.name)) {
          const currentTotalImages = existingImageCount + uploadedImages.length;
          if (currentTotalImages >= MAX_IMAGES_PER_SESSION) {
            uploadWarningRef.current = `Skipped ${file.name} (Limit of 10 images reached).`;
            setWarningMsg(uploadWarningRef.current);
            continue;
          }
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("session_state", sessionState);

        await uploadFileApi(
          selectedUseCase,
          formData,
          idToken,
          sessionState,
          (update: FileProgressUpdate) => {
            const phaseIndex = update.step - 1;
            setPhases((prev) => {
              const updated = [...prev];
              updated[phaseIndex] = prefix + update.message;
              return updated;
            });
            setCurrentPhase(phaseIndex);

            if (update.meta_data?.warning)
              uploadWarningRef.current = update.meta_data.warning;
            if (update.meta_data?.blob_filepath)
              filepath = update.meta_data.blob_filepath ?? "";
            if (update.meta_data?.file_name) {
              filename = update.meta_data.file_name ?? "";
              if (
                imageExtensions.some((ext) =>
                  filename.toLowerCase().endsWith(ext),
                )
              ) {
                image_filename = filename;
              }
            }
          },
        );

        if (filepath) {
          uploadedPaths.push(filepath);

          // Restore original logic: only addItem if the chat already exists in DB
          const sessionStateChat =
            await indexedDBInstance.getChat(sessionState);
          if (sessionStateChat && sessionStateChat.answers.length > 0) {
            await indexedDBInstance.addItem(
              sessionState,
              [],
              [],
              [filepath],
              [filename],
              [image_filename],
            );
          }
        }

        if (filename) uploadedFilenames.push(filename);
        if (image_filename) uploadedImages.push(image_filename);

        setPhases([...defaultPhases]);
        setCurrentPhase(0);
      }

      if (uploadedPaths.length > 0) {
        onUploadComplete(uploadedPaths, uploadedFilenames, uploadedImages);
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = `Upload failed. Check file type (PDF/Office/Image) and name.`;
      setError(errorMessage);
    } finally {
      setCurrentPhase(defaultPhases.length - 1);
      setIsHandling(false);
      if (uploadWarningRef.current) {
        setWarningMsg(uploadWarningRef.current);
      }
    }
  };

  // UPDATED: Now accepts optional knownFilepath
  const handleRemoveFile = async (
    filename: string,
    knownFilepath?: string,
  ) => {
    setIsHandling(true);
    setError(undefined);

    // initialize phases for the delete operation
    setPhases([...defaultPhases]);
    setCurrentPhase(0);

    // Default to known path, fix empty string issue
    let filepath = knownFilepath || "";

    try {
      const token = await getToken(client);
      if (!LOCAL_DEV && !token)
        throw new Error("No authentication token available");

      await deleteUploadedFileApi(
        selectedUseCase,
        undefined,
        filename,
        token,
        sessionState,
        (update: FileProgressUpdate) => {
          // update phases same way as in upload
          const phaseIndex = update.step - 1;
          setPhases((prev) => {
            const updated = [...prev];
            updated[phaseIndex] = update.message;
            return updated;
          });
          setCurrentPhase(phaseIndex);

          // If backend returns path, update it, otherwise keep knownFilepath
          if (update.meta_data?.blob_filepath) {
            filepath = update.meta_data.blob_filepath;
          }
          if (update.meta_data?.warning) {
            uploadWarningRef.current = update.meta_data.warning;
            setWarningMsg(update.meta_data.warning);
          }
        },
      );

      await indexedDBInstance.removeDocument(sessionState, filepath, filename);
      onDocumentDelete(filepath, filename);

      // optional: reset phases after successful delete
      setPhases([...defaultPhases]);
      setCurrentPhase(0);
    } catch (e) {
      console.error(e);
      setError("Failed to delete file.");
    } finally {
      // mark last phase as finished (similar to upload finally)
      setCurrentPhase(defaultPhases.length - 1);
      setIsHandling(false);
      if (uploadWarningRef.current) {
        setWarningMsg(uploadWarningRef.current);
      }
    }
  };

  return {
    uploadedFiles,
    isHandling,
    error,
    warningMsg,
    phases,
    currentPhase,
    handleUploadFiles,
    handleRemoveFile,
    refreshFiles,
  };
};